import { useMemo, useRef } from 'react';
import {
  layoutWorld,
  parseWorldArt,
  type LayoutRegion,
  type Territory,
  type WorldArt,
} from '@map/worldkit';
import type { RegionTree } from '@map/shared';

export interface WorldLayout {
  territories: Map<string, Territory>;
  unplaced: Array<{ id: string; reason: string }>;
}

/**
 * Lays the region tree out on the planet's art, warm-starting from the previous
 * layout so that changing a weight or adding a region shifts borders rather
 * than rearranging the continent.
 */
export function useWorldLayout(art: WorldArt | null, tree: RegionTree | null): WorldLayout | null {
  const previous = useRef<Map<string, { site: Territory['site']; weight: number }>>(new Map());
  const shapes = useRef<Map<string, string>>(new Map());

  return useMemo(() => {
    if (!art || !tree) return null;

    const regions = new Map<string, LayoutRegion>();
    for (const [id, node] of Object.entries(tree.regions)) {
      regions.set(id, {
        id,
        parentId: node.parentId,
        name: node.name,
        kind: node.kind,
        importanceWeight: node.importanceWeight,
        artSlotKey: node.artSlotKey,
        geometrySeed: node.geometrySeed,
        geometryScale: node.geometryScale,
        children: node.children,
      });
    }

    // A place asked for a new shape must not be warm-started back into its old
    // one: forget the previous sites of everything that shape was dealt from.
    for (const [id, node] of Object.entries(tree.regions)) {
      const shape = `${node.geometrySeed}:${node.geometryScale}`;
      const before = shapes.current.get(id);
      shapes.current.set(id, shape);
      if (before === undefined || before === shape) continue;
      const forget = (regionId: string) => {
        previous.current.delete(regionId);
        for (const child of tree.regions[regionId]?.children ?? []) forget(child);
      };
      forget(node.parentId ?? id);
    }

    const result = layoutWorld(art, regions, tree.roots, { previous: previous.current });

    previous.current = new Map(
      [...result.territories].map(([id, t]) => [id, { site: t.site, weight: t.weight }]),
    );

    return result;
  }, [art, tree]);
}

const cache = new Map<string, Promise<WorldArt>>();

/** World art is immutable and hashed, so it is fetched once and kept. */
export function loadWorldArt(key: string): Promise<WorldArt> {
  const existing = cache.get(key);
  if (existing) return existing;

  const request = fetch(`/worlds/${key}.json`)
    .then((res) => {
      if (!res.ok) throw new Error(`world art "${key}" could not be loaded`);
      return res.json();
    })
    .then((raw: unknown) => {
      const parsed = parseWorldArt(raw);
      if ('issues' in parsed) {
        throw new Error(`world art "${key}" is invalid: ${parsed.issues[0]?.message ?? 'unknown'}`);
      }
      return parsed.art;
    })
    .catch((err: unknown) => {
      cache.delete(key);
      throw err;
    });

  cache.set(key, request);
  return request;
}
